import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft, Search, Star, Calendar, Award,
  TrendingUp, Users, Flame, Loader2, Download,
  Filter, ChevronDown, Eye
} from 'lucide-react';
import { useProgressOverview } from '../../hooks/useTeacher';

const AVATAR_EMOJIS = {
  boy: '👦',
  girl: '👧',
  seedling: '🌱',
  tree: '🌳',
  flower: '🌻',
  bee: '🐝',
};

const GRADE_FILTERS = ['All Grades', 'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5', 'Grade 6', 'Grade 7', 'Grade 8'];

export default function Progress() {
  const { data: progressData, isLoading, isError } = useProgressOverview();
  const [searchQuery, setSearchQuery] = useState('');
  const [gradeFilter, setGradeFilter] = useState('All Grades');
  const [sortBy, setSortBy] = useState('name'); // name, points, progress, week

  const students = progressData?.students || [];

  // Filter and sort students
  const filteredStudents = students
    .filter((student) => {
      const matchesSearch = student.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesGrade = gradeFilter === 'All Grades' || student.grade === gradeFilter;
      return matchesSearch && matchesGrade;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'points':
          return b.total_points - a.total_points;
        case 'progress':
          return b.progress_percent - a.progress_percent;
        case 'week':
          return b.current_week - a.current_week;
        default:
          return a.name.localeCompare(b.name);
      }
    });

  const getProgressColor = (percent) => {
    if (percent >= 75) return 'bg-leaf-500';
    if (percent >= 50) return 'bg-sunshine-500';
    if (percent >= 25) return 'bg-orange-500';
    return 'bg-red-400';
  };

  const getGradeLabel = (percent) => {
    if (percent >= 90) return { label: 'A', color: 'text-leaf-600 bg-leaf-100' };
    if (percent >= 80) return { label: 'B+', color: 'text-blue-600 bg-blue-100' };
    if (percent >= 70) return { label: 'B', color: 'text-blue-600 bg-blue-100' };
    if (percent >= 60) return { label: 'C+', color: 'text-yellow-600 bg-yellow-100' };
    if (percent >= 50) return { label: 'C', color: 'text-yellow-600 bg-yellow-100' };
    if (percent >= 40) return { label: 'D', color: 'text-orange-600 bg-orange-100' };
    return { label: 'E', color: 'text-red-600 bg-red-100' };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-leaf-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading progress data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Link
              to="/teacher"
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <ChevronLeft size={20} className="text-gray-600" />
            </Link>
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Student Progress</h1>
              <p className="text-gray-600">View all students' progress and grades</p>
            </div>
            <button className="btn-ghost flex items-center gap-2">
              <Download size={18} />
              Export
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm p-5"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Users size={20} className="text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{progressData?.total || 0}</p>
                <p className="text-sm text-gray-500">Total Students</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white rounded-xl shadow-sm p-5"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-leaf-100 flex items-center justify-center">
                <TrendingUp size={20} className="text-leaf-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{progressData?.class_avg_completion || 0}%</p>
                <p className="text-sm text-gray-500">Avg. Completion</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-sm p-5"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-sunshine-100 flex items-center justify-center">
                <Star size={20} className="text-sunshine-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{progressData?.class_avg_points || 0}</p>
                <p className="text-sm text-gray-500">Avg. Points</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-sm p-5"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Award size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {students.reduce((sum, s) => sum + s.badges_earned, 0)}
                </p>
                <p className="text-sm text-gray-500">Total Badges</p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-2.5 rounded-lg border border-gray-200 focus:border-leaf-500 focus:ring-2 focus:ring-leaf-200"
              />
            </div>

            {/* Grade Filter */}
            <div className="relative">
              <select
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2.5 rounded-lg border border-gray-200 focus:border-leaf-500 focus:ring-2 focus:ring-leaf-200 bg-white"
              >
                {GRADE_FILTERS.map((grade) => (
                  <option key={grade} value={grade}>{grade}</option>
                ))}
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>

            {/* Sort */}
            <div className="relative">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="appearance-none pl-4 pr-10 py-2.5 rounded-lg border border-gray-200 focus:border-leaf-500 focus:ring-2 focus:ring-leaf-200 bg-white"
              >
                <option value="name">Sort by Name</option>
                <option value="points">Sort by Points</option>
                <option value="progress">Sort by Progress</option>
                <option value="week">Sort by Week</option>
              </select>
              <ChevronDown size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Students Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Grade
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Week
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Points
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Badges
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Streak
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Performance
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredStudents.map((student, index) => {
                  const gradeInfo = getGradeLabel(student.progress_percent);
                  return (
                    <motion.tr
                      key={student.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.03 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-leaf-100 flex items-center justify-center text-xl">
                            {AVATAR_EMOJIS[student.avatar] || '🌱'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{student.name}</p>
                            <p className="text-sm text-gray-500">{student.grade || 'No grade'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-600">{student.grade || '-'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="w-32">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-600">{student.weeks_completed}/36</span>
                            <span className="font-medium">{student.progress_percent}%</span>
                          </div>
                          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${getProgressColor(student.progress_percent)}`}
                              style={{ width: `${student.progress_percent}%` }}
                            />
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Calendar size={14} className="text-blue-500" />
                          <span className="font-medium">{student.current_week}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Star size={14} className="text-sunshine-500" />
                          <span className="font-semibold text-sunshine-600">{student.total_points}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Award size={14} className="text-purple-500" />
                          <span className="font-medium">{student.badges_earned}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Flame size={14} className="text-orange-500" />
                          <span className="font-medium">{student.streak_days}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <span className={`px-3 py-1 rounded-full text-sm font-bold ${gradeInfo.color}`}>
                          {gradeInfo.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <Link
                          to={`/teacher/students/${student.id}`}
                          className="inline-flex items-center gap-1 text-leaf-600 hover:text-leaf-700 text-sm font-medium"
                        >
                          <Eye size={16} />
                          View
                        </Link>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredStudents.length === 0 && (
            <div className="text-center py-12">
              <Users size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500">No students found</p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-2 text-leaf-600 hover:text-leaf-700"
                >
                  Clear search
                </button>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
